
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FormDescription } from "@/components/ui/form";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { cn } from "@/lib/utils";

// Form validation schema
const leadAccountSchema = z.object({
  external_id: z.string().min(1, "External ID is required"),
  exchange: z.string().min(1, "Exchange is required"),
  active: z.boolean().default(true),
  apiKey: z.string().optional(),
  apiSecret: z.string().optional(),
});

type LeadAccountFormValues = z.infer<typeof leadAccountSchema>;

interface LeadAccountFormProps {
  onSubmit: (data: any, isValid: boolean) => void;
  initialData?: any;
}

export const LeadAccountForm = ({ onSubmit, initialData }: LeadAccountFormProps) => {
  const [showApiSecret, setShowApiSecret] = useState(false);
  
  // Create form with validation
  const form = useForm<LeadAccountFormValues>({
    resolver: zodResolver(leadAccountSchema),
    defaultValues: {
      external_id: initialData?.external_id || '',
      exchange: initialData?.exchange || '',
      active: initialData?.active !== false,
      apiKey: initialData?.apiKey || '',
      apiSecret: initialData?.apiSecret || '',
    },
  });
  
  // Notify parent component about form validity changes
  useEffect(() => {
    const subscription = form.watch(() => {
      if (Object.keys(form.formState.touchedFields).length > 0) {
        const formData = form.getValues();
        onSubmit({
          leadAccount: {
            external_id: formData.external_id,
            exchange: formData.exchange,
            active: formData.active,
            apiKey: formData.apiKey,
            apiSecret: formData.apiSecret
          }
        }, form.formState.isValid);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, onSubmit]);
  
  const handleFormSubmit = (data: LeadAccountFormValues) => {
    onSubmit({
      leadAccount: {
        external_id: data.external_id,
        exchange: data.exchange,
        active: data.active,
        apiKey: data.apiKey,
        apiSecret: data.apiSecret
      }
    }, true);
  };

  const toggleApiSecretVisibility = () => {
    setShowApiSecret(!showApiSecret);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="external_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External ID <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      The unique identifier for this lead account in the external system
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="exchange"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Exchange <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select exchange" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OKX">OKX</SelectItem>
                          <SelectItem value="Bybit">Bybit</SelectItem>
                          <SelectItem value="Bitget">Bitget</SelectItem>
                          <SelectItem value="Phemex">Phemex</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      The exchange platform where the lead account is located
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Key (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      API key for programmatic access to the lead account
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="apiSecret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Secret (optional)</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input 
                          {...field} 
                          type={showApiSecret ? "text" : "password"}
                          className="pr-10"
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={toggleApiSecretVisibility}
                      >
                        {showApiSecret ? (
                          <EyeOffIcon className="h-4 w-4" />
                        ) : (
                          <EyeIcon className="h-4 w-4" />
                        )}
                        <span className="sr-only">
                          {showApiSecret ? "Hide API secret" : "Show API secret"}
                        </span>
                      </Button>
                    </div>
                    <FormDescription>
                      API secret for programmatic access to the lead account (stored securely)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Account Active</FormLabel>
                      <FormDescription>
                        Active lead accounts can be used to create trading strategies
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch 
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};
